/**
 * Save to Journal API — uploads garden snapshot to Firebase Storage and creates plan doc in Firestore.
 */

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, doc, getDocs, setDoc } from 'firebase/firestore';
import { db, storage, auth } from './firebase-config.js';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const MAX_JOURNAL_PLANS = 20;

/** Storage path for a plan’s snapshot image (used by main app and dashboard). Saved as WebP. */
export function getPlanSnapshotStoragePath(uid, planId) {
    return `users/${uid}/gardenPlans/${planId}/snapshot.webp`;
}

/** Storage path for a plan’s Garden Plant Information snapshot (used by dashboard). Saved as WebP. */
export function getPlanPlantInfoStoragePath(uid, planId) {
    return `users/${uid}/gardenPlans/${planId}/plant-info.webp`;
}

/** Storage path for a plan’s garden calendar snapshot (used by dashboard). Saved as WebP. */
export function getPlanCalendarStoragePath(uid, planId) {
    return `users/${uid}/gardenPlans/${planId}/calendar.webp`;
}

/** Storage path for a journal entry's photo (used by dashboard). Saved as WebP. */
export function getJournalEntryPhotoPath(uid, planId, entryId) {
    return `users/${uid}/gardenPlans/${planId}/journalEntries/${entryId}/photo.webp`;
}

export async function getUserGardenPlanCount() {
    const user = auth?.currentUser;
    if (!user) throw new Error('You must be signed in to save to journal');
    if (!db) throw new Error('Firebase not initialized');
    const snap = await getDocs(collection(db, 'users', user.uid, 'gardenPlans'));
    return snap.size;
}

/**
 * Ensure the user does not already have a garden plan with the given name (case-insensitive).
 * Used to require unique garden bed names in the journal.
 */
export async function assertGardenPlanNameIsUnique(name) {
    const user = auth?.currentUser;
    if (!user) throw new Error('You must be signed in to save to journal');
    if (!db) throw new Error('Firebase not initialized');
    const trimmed = String(name || '').trim();
    if (!trimmed) return;

    const snap = await getDocs(collection(db, 'users', user.uid, 'gardenPlans'));
    const lower = trimmed.toLowerCase();
    let hasDuplicate = false;
    snap.forEach(docSnap => {
        if (hasDuplicate) return;
        const data = docSnap.data?.() || docSnap.data();
        const title = data && typeof data.title === 'string' ? data.title.trim() : '';
        if (title && title.toLowerCase() === lower) {
            hasDuplicate = true;
        }
    });

    if (hasDuplicate) {
        throw new Error(`You already have a garden bed named "${trimmed}". Please use a unique name.`);
    }
}

export async function assertGardenPlanLimitNotReached(limit = MAX_JOURNAL_PLANS) {
    const count = await getUserGardenPlanCount();
    if (count >= limit) {
        throw new Error(`You have reached the maximum of ${limit} saved garden plans. Remove one from your Dashboard before adding another.`);
    }
}

/**
 * Upload snapshot to Storage and create garden plan in Firestore.
 * @param {{ name: string, plants: string[], plantCounts?: Record<string, number>, plantBaseByDisplay?: Record<string, string>, imageBlob: Blob, plantInfoBlob?: Blob, calendarBlob?: Blob, year?: number }} opts
 *   plants — unique canonical crop names in first-seen grid order (matches Garden Plant Information summary).
 *   plantCounts — per-crop totals (all varieties of the same crop aggregated).
 *   plantBaseByDisplay — optional legacy map from saves that keyed rows by variety label (older journal entries).
 * @returns {Promise<{ planId: string }>}
 */
export async function savePlanSnapshotToJournal({ name, plants, plantCounts, plantBaseByDisplay, imageBlob, plantInfoBlob, calendarBlob, year: optsYear }) {
    const user = auth?.currentUser;
    if (!user) throw new Error('You must be signed in to save to journal');
    if (!db || !storage) throw new Error('Firebase not initialized');
    await assertGardenPlanLimitNotReached();
    const uid = user.uid;
    const planId = crypto.randomUUID?.() || `plan_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const now = new Date();
    const year = typeof optsYear === 'number' && optsYear >= 2020 && optsYear <= 2100 ? optsYear : now.getFullYear();
    const month = MONTHS[now.getMonth()];

    const storagePath = getPlanSnapshotStoragePath(uid, planId);
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, imageBlob, { contentType: 'image/webp' });
    const snapshotUrl = await getDownloadURL(storageRef);

    let plantInfoImage = null;
    if (plantInfoBlob) {
        const plantInfoPath = getPlanPlantInfoStoragePath(uid, planId);
        const plantInfoRef = ref(storage, plantInfoPath);
        await uploadBytes(plantInfoRef, plantInfoBlob, { contentType: plantInfoBlob.type || 'image/webp' });
        plantInfoImage = await getDownloadURL(plantInfoRef);
    }

    let calendarImage = null;
    if (calendarBlob) {
        const calendarPath = getPlanCalendarStoragePath(uid, planId);
        const calendarRef = ref(storage, calendarPath);
        await uploadBytes(calendarRef, calendarBlob, { contentType: calendarBlob.type || 'image/webp' });
        calendarImage = await getDownloadURL(calendarRef);
    }

    const hasPlantCounts = plantCounts && typeof plantCounts === 'object' && Object.keys(plantCounts).length > 0;
    const hasPlantBaseMap = plantBaseByDisplay && typeof plantBaseByDisplay === 'object' && Object.keys(plantBaseByDisplay).length > 0;
    const planDoc = {
        id: planId,
        title: name,
        year,
        month,
        plants: plants || [],
        ...(hasPlantCounts ? { plantCounts } : {}),
        ...(hasPlantBaseMap ? { plantBaseByDisplay } : {}),
        status: 'Active',
        image: snapshotUrl,
        ...(plantInfoImage && { plantInfoImage }),
        ...(calendarImage && { calendarImage }),
        createdAt: now.toISOString()
    };
    const planRef = doc(db, 'users', uid, 'gardenPlans', planId);
    await setDoc(planRef, planDoc);

    // Keep user doc identifiable by email in Firestore console (document ID stays UID for security)
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, {
        email: user.email || null,
        updatedAt: now.toISOString()
    }, { merge: true });

    return { planId };
}
